import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/app/AuthContext";

/**
 * The organization context for the dashboard's org-scoped data (units, stats,
 * sessions, billing). M:N-aware:
 *
 *   system admin → dashboardOrgId = null  → fleet-wide global endpoints
 *   manager      → dashboardOrgId = the SELECTED managed org → /api/org/<id>/...
 *                  (auto-selected when there is exactly one; a selector chooses
 *                  among several; driver-only orgs never appear)
 *
 * There is no singular user.organization_id — the manageable set comes from the
 * user's manager memberships. Changing the selection changes the value, which is
 * woven into each query key so React Query refetches every org-scoped query.
 */
interface DashboardOrgValue {
  /** The org_id to scope dashboard data to, or null for a fleet (admin) view. */
  dashboardOrgId: number | null;
  /**
   * Whether it is SAFE to issue dashboard queries. A manager is NOT ready until an
   * authorized manager org is selected — so a manager NEVER fires a fleet request
   * during the brief pre-selection window, and a driver-only user fires nothing.
   * A system admin is always ready (fleet view).
   */
  scopeReady: boolean;
  /** True only for a system admin: dashboardOrgId=null means the whole fleet. For a
   *  non-admin, null is NEVER "fleet" — it just means "not ready yet". */
  isFleet: boolean;
  /** Org ids the user may manage (empty for a system admin → fleet view). */
  manageableOrgIds: number[];
  /** True when the manager may choose between multiple orgs. */
  isMultiOrg: boolean;
  setDashboardOrgId: (orgId: number) => void;
}

const Ctx = createContext<DashboardOrgValue>({
  dashboardOrgId: null,
  scopeReady: false,
  isFleet: false,
  manageableOrgIds: [],
  isMultiOrg: false,
  setDashboardOrgId: () => {},
});

export function DashboardOrgProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin, managerOrgIds } = useAuth();
  const [selected, setSelected] = useState<number | null>(null);

  // Membership reconciliation: discard a no-longer-manageable selection; auto-select
  // the sole manager org; for several, keep a prior selection only if still
  // authorized; never select a driver-only membership (managerOrgIds is the manager
  // set only). Admins keep null (fleet).
  useEffect(() => {
    if (isAdmin || managerOrgIds.length === 0) {
      setSelected(null);
      return;
    }
    setSelected((cur) => (cur != null && managerOrgIds.includes(cur) ? cur : managerOrgIds[0]));
  }, [isAdmin, managerOrgIds.join(",")]);

  const value = useMemo<DashboardOrgValue>(() => {
    const dashboardOrgId = isAdmin ? null : selected;
    // A manager is ready only once a VALID manager org is selected; an admin is
    // always ready. null is never interpreted as "fleet" for a non-admin.
    const scopeReady = isAdmin || (selected != null && managerOrgIds.includes(selected));
    return {
      dashboardOrgId,
      scopeReady,
      isFleet: isAdmin,
      manageableOrgIds: isAdmin ? [] : managerOrgIds,
      isMultiOrg: !isAdmin && managerOrgIds.length > 1,
      setDashboardOrgId: setSelected,
    };
  }, [isAdmin, selected, managerOrgIds.join(",")]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDashboardOrg() {
  return useContext(Ctx);
}
